"use client";

import { CrossIcon } from "@components/icons";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from "@nextui-org/react";
import { useEffect } from "react";

/**
 * CustomDropdown component renders a dropdown menu with a trigger element.
 *
 * Example usage:
 * 
 * const dropdownElements = (
 *   <>
 *     <DropdownItem>Item 1</DropdownItem>
 *     <DropdownItem>Item 2</DropdownItem>
 *   </>
 * );
 * 
 * const triggerElement = <span>Menu</span>;
 * 
 * return (
 *   <CustomDropdown triggerElementContent={triggerElement} dropdownElements={dropdownElements} />
 * );
 * 
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.triggerElementContent - The element content to be used as the trigger for the dropdown.
 * @param {React.ReactNode} props.dropdownElements - The elements to be displayed inside the dropdown menu.
 * @param {function} props.setIsOpen - Function to set the open state of the dropdown.
 * @param {boolean} props.isOpen - Boolean indicating whether the dropdown is open.
 * @param {function} props.toggleDropdown - Function to toggle the open state of the dropdown.
 * @param {string} [props.className] - Additional class names to apply to the dropdown.
 * 
 * @returns {JSX.Element} The rendered CustomDropdown component.
 */
export function CustomDropdown({ triggerElementContent, dropdownElements, setIsOpen, isOpen, toggleDropdown, className = "" }) {

    useEffect(() => {
        const handleResize = () => {
            setIsOpen(false);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [setIsOpen]);

    return (
        <Dropdown
            isOpen={isOpen}
            backdrop="blur"
            className={`bg-emerald-700 sm:hidden ${className}`}
        >
            <DropdownTrigger>
                <Button
                    variant="light"
                    className={`h-9 min-w-10 border-1 border-emerald-800/60 p-0 outline-none lg:hidden ${!dropdownElements.length ? "hidden" : ""}`}
                    size="sm"
                    onClick={toggleDropdown}
                >
                    {triggerElementContent}
                </Button>
            </DropdownTrigger>
            <DropdownMenu variant="flat" aria-label="Static Actions" className="group max-h-[50vh] overflow-auto p-2">
                <DropdownItem className="relative !p-0" onClick={() => setIsOpen(false)}>
                    <div className="fixed -top-8 right-14 flex size-4 items-center gap-x-1">
                        <p className="text-white">Fermer</p>
                        <CrossIcon className="stroke-white stroke-[3px]" />
                    </div>
                </DropdownItem>
                {dropdownElements}
            </DropdownMenu>
        </Dropdown>
    );
}